// client/src/components/pages/RouteIntelligence/MonitorSettingsModal.tsx (converted from .js, ST-00216)
import { useState, useEffect, type ComponentType, type SyntheticEvent } from 'react';
import { X, Plus, Settings } from 'lucide-react';
import api from '../../../services/api';
import ThemedSelectJs from '../../common/ThemedSelect';
import ThemedTimePicker from '../../common/ThemedTimePicker';
import type { ThemedSelectProps } from '../../../types/commonComponents';
import type {
  MonitorApiError,
  MonitorGroupingOption,
  VarianceMonitor,
  VarianceMonitorPayload,
} from '../../../types/clientPages/routeIntelligence';

// ThemedSelect (still JS): the grouping id is a number when editing, and `required` is passed through.
const ThemedSelect = ThemedSelectJs as unknown as ComponentType<
  Omit<ThemedSelectProps, 'value'> & { value: string | number; required?: boolean }
>;

interface MonitorSettingsModalProps {
  /** The monitor being edited; null/undefined = add a new one. */
  editMonitor?: VarianceMonitor | null;
  onClose: () => void;
  onSaved: () => void;
}

const MonitorSettingsModal = ({ editMonitor, onClose, onSaved }: MonitorSettingsModalProps) => {
  const isEditing = !!editMonitor;
  const [groupings, setGroupings] = useState<MonitorGroupingOption[]>([]);
  const [existingGroupingIds, setExistingGroupingIds] = useState<Set<number | undefined>>(
    new Set()
  );
  const [loadingGroupings, setLoadingGroupings] = useState(true);
  const [groupingId, setGroupingId] = useState<number | string>(editMonitor?.grouping_id || '');
  const [varianceThreshold, setVarianceThreshold] = useState<number | string>(
    editMonitor?.variance_threshold ?? 2.0
  );
  const [streakDaysToAlert, setStreakDaysToAlert] = useState<number | string>(
    editMonitor?.streak_days_to_alert ?? 2
  );
  const [lookbackDays, setLookbackDays] = useState<number | string>(
    editMonitor?.lookback_days ?? 40
  );
  const [scheduleTime, setScheduleTime] = useState(editMonitor?.schedule_time || '07:00');
  const [emailRecipients, setEmailRecipients] = useState(editMonitor?.email_recipients || '');
  const [isActive, setIsActive] = useState(editMonitor?.is_active !== false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const [g, m] = await Promise.all([
          api.get('/route-summary/groupings'),
          api.routeIntelligence.getMonitors(),
        ]);
        setGroupings((g.data as MonitorGroupingOption[]) || []);
        setExistingGroupingIds(
          new Set(((m.data as VarianceMonitor[]) || []).map((x) => x.grouping_id))
        );
      } catch {
      } finally {
        setLoadingGroupings(false);
      }
    })();
  }, []);

  const availableGroupings = groupings.filter((g) =>
    isEditing ? true : !existingGroupingIds.has(g.id || g.grouping_id)
  );

  const handleSubmit = async (e?: SyntheticEvent) => {
    if (e) e.preventDefault();
    if (!groupingId) {
      setError('Select a route grouping');
      return;
    }
    setSaving(true);
    setError(null);
    const data: VarianceMonitorPayload = {
      groupingId: parseInt(String(groupingId)),
      varianceThreshold: parseFloat(String(varianceThreshold)),
      streakDaysToAlert: parseInt(String(streakDaysToAlert)),
      lookbackDays: parseInt(String(lookbackDays)),
      scheduleTime,
      emailRecipients: emailRecipients.trim() || null,
      isActive,
    };
    try {
      if (isEditing) await api.routeIntelligence.updateMonitor(editMonitor.monitor_id, data);
      else await api.routeIntelligence.createMonitor(data);
      onSaved();
    } catch (err) {
      setError(
        (err as MonitorApiError).response?.status === 409
          ? 'Monitor already exists for this grouping.'
          : (err as MonitorApiError).response?.data?.error || 'Failed to save'
      );
    } finally {
      setSaving(false);
    }
  };

  const inputCls =
    'w-full h-9 px-3 bg-surface border border-gray-200 rounded-md text-sm text-slate-800 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20 focus:outline-none disabled:bg-gray-50 disabled:text-gray-400';

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-surface rounded-lg border border-gray-200 w-full max-w-md max-h-[90vh] flex flex-col shadow-xl">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-2">
            {isEditing ? (
              <Settings className="w-4 h-4 text-gray-400" />
            ) : (
              <Plus className="w-4 h-4 text-primary-500" />
            )}
            <h2 className="text-sm font-semibold text-slate-800">
              {isEditing ? 'Edit Monitor' : 'Add Monitor'}
            </h2>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100">
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-auto px-5 py-4 space-y-4">
          {error && (
            <div className="p-2.5 bg-red-50 border border-red-200 rounded-md text-xs text-red-600">
              {error}
            </div>
          )}

          <div>
            <label className="block text-[11px] font-medium text-gray-500 uppercase tracking-wider mb-1.5">
              Route Grouping
            </label>
            {loadingGroupings ? (
              <div className="h-9 bg-gray-50 animate-pulse rounded-md" />
            ) : (
              <ThemedSelect
                value={groupingId}
                onChange={(e) => setGroupingId(e.target.value)}
                disabled={isEditing}
                className={inputCls}
                required
              >
                <option value="">Select...</option>
                {availableGroupings.map((g) => (
                  <option key={g.id || g.grouping_id} value={g.id || g.grouping_id}>
                    {g.name || g.grouping_name}
                  </option>
                ))}
              </ThemedSelect>
            )}
          </div>

          <div className="pt-2">
            <div className="text-[10px] font-semibold text-gray-300 uppercase tracking-wider mb-3">
              Detection
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] font-medium text-gray-500 mb-1.5">
                  Threshold (%)
                </label>
                <input
                  type="number"
                  value={varianceThreshold}
                  onChange={(e) => setVarianceThreshold(e.target.value)}
                  min="0.1"
                  step="0.1"
                  className={inputCls}
                />
              </div>
              <div>
                <label className="block text-[11px] font-medium text-gray-500 mb-1.5">
                  Alert After (days)
                </label>
                <input
                  type="number"
                  value={streakDaysToAlert}
                  onChange={(e) => setStreakDaysToAlert(e.target.value)}
                  min="1"
                  max="30"
                  className={inputCls}
                />
              </div>
            </div>
          </div>

          <div className="pt-2">
            <div className="text-[10px] font-semibold text-gray-300 uppercase tracking-wider mb-3">
              Schedule
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] font-medium text-gray-500 mb-1.5">
                  Lookback (days)
                </label>
                <input
                  type="number"
                  value={lookbackDays}
                  onChange={(e) => setLookbackDays(e.target.value)}
                  min="7"
                  max="90"
                  className={inputCls}
                />
              </div>
              <div>
                {/* The scheduler reads this as SAST wall-clock time (ST-00215). */}
                <label className="block text-[11px] font-medium text-gray-500 mb-1.5">
                  Daily Run Time (SAST)
                </label>
                <ThemedTimePicker
                  value={scheduleTime}
                  onChange={(e) => setScheduleTime(e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="pt-2">
            <label className="block text-[11px] font-medium text-gray-500 mb-1.5">
              Email Recipients
            </label>
            <textarea
              value={emailRecipients}
              onChange={(e) => setEmailRecipients(e.target.value)}
              placeholder="email@example.com, ..."
              rows={2}
              className="w-full px-3 py-2 bg-surface border border-gray-200 rounded-md text-sm text-slate-800 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20 focus:outline-none placeholder:text-gray-400 resize-none"
            />
            <p className="text-[10px] text-gray-300 mt-1">
              Comma-separated. Sent when streak threshold is reached.
            </p>
          </div>

          <div className="flex items-center justify-between py-2.5 px-3 bg-gray-50 rounded-md">
            <div>
              <span className="text-xs font-medium text-slate-800">Active</span>
              <p className="text-[10px] text-gray-400">Enable scheduled checks</p>
            </div>
            <button
              type="button"
              onClick={() => setIsActive(!isActive)}
              className={`relative w-9 h-5 rounded-full transition-colors ${isActive ? 'bg-primary-500' : 'bg-gray-300'}`}
            >
              <span
                className={`absolute top-0.5 h-4 w-4 rounded-full bg-surface shadow transition-transform ${isActive ? 'left-[18px]' : 'left-0.5'}`}
              />
            </button>
          </div>
        </form>

        <div className="flex justify-end gap-2 px-5 py-3 border-t border-gray-100 flex-shrink-0">
          <button
            onClick={onClose}
            className="h-8 px-3 text-xs text-gray-500 bg-gray-100 hover:bg-gray-200 rounded-md"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="h-8 px-4 text-xs font-semibold text-white rounded-md disabled:opacity-50 transition-colors bg-primary-500"
          >
            {saving ? 'Saving...' : isEditing ? 'Save Changes' : 'Create Monitor'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MonitorSettingsModal;
